import React from "react";
import { Star, ArrowUpRight } from "lucide-react";
import { SITE } from "@/lib/site";

// Google "G" wordmark rendered as inline SVG (official colored letter G).
const GoogleG = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

/**
 * Compact Google Reviews rating badge.
 * Renders as an <a> that deep-links to the live Google Business Profile.
 */
export const GoogleRatingBadge = ({
  size = "md",
  layout = "row",
  showCtaText = true,
  className = "",
  testId = "google-rating-badge",
}) => {
  const isLarge = size === "lg";
  const starSize = isLarge ? 18 : 15;

  return (
    <a
      href={SITE.googleReviewsUrl}
      target="_blank"
      rel="noreferrer noopener"
      data-testid={testId}
      className={`group inline-flex ${
        layout === "row" ? "flex-row items-center" : "flex-col items-start"
      } gap-4 bg-white border border-[#EAEAEA] rounded-2xl px-5 py-3.5 hover:border-[#0F1115] hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all ${className}`}
      aria-label={`Read all ${SITE.googleReviewCount} Google Reviews`}
    >
      <div className="flex items-center gap-3">
        <GoogleG size={isLarge ? 22 : 18} />
        <div className="leading-tight">
          <div className={`font-display font-black text-[#0F1115] ${isLarge ? "text-[26px]" : "text-[20px]"}`}>
            {SITE.googleRating.toFixed(1)}
            <span className="text-[13px] text-[#4A4A4A] font-normal font-body ml-1">/ 5</span>
          </div>
          <div className="flex text-[#FDD805] mt-0.5" aria-hidden>
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} size={starSize} fill="#FDD805" strokeWidth={0} />
            ))}
          </div>
        </div>
      </div>

      <div className={`${layout === "row" ? "pl-4 border-l border-[#EAEAEA]" : ""}`}>
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#4A4A4A]">
          Google Reviews
        </div>
        <div className={`text-[#0F1115] font-medium ${isLarge ? "text-[15px]" : "text-[13px]"}`}>
          {SITE.googleReviewCount} verified reviews
        </div>
        {showCtaText && (
          <div className="mt-1 text-[12px] font-bold text-[#0F1115] inline-flex items-center gap-1 group-hover:gap-2 transition-[gap]">
            Read on Google <ArrowUpRight size={12} />
          </div>
        )}
      </div>
    </a>
  );
};

export default GoogleRatingBadge;
